import { PieChart, Share2 } from '@tamagui/lucide-icons';
import { useQueryClient } from '@tanstack/react-query';
import { Image } from '@unistocks/components/basic/image';
import { ScrollViewStyles } from '@unistocks/components/basic/scrollview';
import { EmptyList } from '@unistocks/components/no-data/empty-list';
import { Page } from '@unistocks/components/page/page';
import { SharedPortfolioDetails } from '@unistocks/features/portfolios/components/portfolio-details/shared-portfolio-details';
import {
  getPortfolioTableItemType,
  keyExtractor,
  PortfolioTableFooter,
  renderPortfolioTableItem,
  usePortfolioTableData
} from '@unistocks/features/portfolios/components/portfolio-table/portfolio-table';
import {
  sharedPortfoliosGetTimeSeriesQueryKey,
  useSharedPortfoliosGetSharedPortfolioQuery,
  useSharedPortfoliosGetSharedPortfolioSettingsQuery
} from '@unistocks/proxy/axios-client/SharedPortfoliosQuery';
import { t } from '@unistocks/shared/localization/index.ts';
import { HasId } from '@unistocks/types/has-id';
import { useCallback } from 'react';
import { Separator } from 'tamagui';

export const SharedPortfolio: React.FC<HasId> = ({ id }) => {
  const queryClient = useQueryClient();

  const {
    data: portfolio,
    isPending,
    isSuccess,
    refetch: refetchPortfolio,
    isRefetching
  } = useSharedPortfoliosGetSharedPortfolioQuery(id);

  const {
    data: sharedPortfolio,
    isSuccess: isSharedPortfolioSuccess,
    refetch: refetchSharedPortfolio
  } = useSharedPortfoliosGetSharedPortfolioSettingsQuery(id);

  const tableData = usePortfolioTableData(portfolio);

  const refresh = useCallback(() => {
    refetchPortfolio();
    refetchSharedPortfolio();
    queryClient.invalidateQueries({
      queryKey: sharedPortfoliosGetTimeSeriesQueryKey(id)
    });
  }, [id, queryClient, refetchPortfolio, refetchSharedPortfolio]);

  return (
    <Page>
      <Page.VirtualList
        headerTitle={portfolio?.name}
        headerIcon={
          sharedPortfolio?.userProfilePictureUri ? (
            <Image rounded size={'$2.5'} source={sharedPortfolio.userProfilePictureUri} />
          ) : null
        }
        breadcrumbs={[
          {
            key: 'shared-portfolios',
            rootPathName: 'shared-portfolios',
            name: t('::Menu:SharedPortfolios'),
            href: '/(tabs)/(menu)/shared-portfolios',
            Icon: props => <Share2 {...props} />
          }
        ]}
        data={tableData}
        extraData={portfolio}
        keyExtractor={keyExtractor}
        getItemType={getPortfolioTableItemType}
        ListHeaderComponent={
          isSuccess && isSharedPortfolioSuccess && tableData.length ? (
            <Page.Content pt={0}>
              <SharedPortfolioDetails portfolio={portfolio} settings={sharedPortfolio.settings} />
            </Page.Content>
          ) : null
        }
        renderItem={renderPortfolioTableItem}
        ListFooterComponent={
          isSuccess && tableData.length ? <PortfolioTableFooter portfolio={portfolio} /> : null
        }
        ItemSeparatorComponent={Separator}
        ListEmptyComponent={
          <EmptyList
            isLoading={isPending}
            noDataText={t('::Portfolio:PortfolioTable:NoData')}
            noDataIcon={PieChart}
          />
        }
        refreshing={isRefetching}
        onRefresh={refresh}
        contentContainerStyle={ScrollViewStyles.contentContainerStyle}
      />
    </Page>
  );
};

SharedPortfolio.displayName = 'SharedPortfolio';
